import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';

import {
  GQLAccessoryConfigurationInput,
  GQLAccessoryModel,
  GQLAccessoryModelInput,
  GQLAccessorySize,
  GQLAccessoryWeightInput,
  GQLMaterialType,
  GQLParty,
  GQLPlatingType
} from '@jewelutions/core-crm-server-api-client-domain';

import { JwlField, JwlSelect } from '../../../../components';
import { SelectOption } from '../../../../components/JwlSelect/JwlSelect';
import { stringListToDisplayValueList } from '../../../../core/utils';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { ErrorMessage } from '../../../../types-hack';

interface IProps {
  accessoryId: string;
  accessoryModel: GQLAccessoryModel;
  party: GQLParty;
  onClose: (success: boolean) => void;
}

const availableAccessorySizes: SelectOption[] = stringListToDisplayValueList(Object.keys(GQLAccessorySize));

const AccessoryModelUpdateDialog: React.FC<IProps> = ({ accessoryId, accessoryModel, party, onClose }) => {
  const match: any = useRouteMatch();

  const [saveAccessoryModel, { error, loading }] = GQLOperationBuilder.accessory.updateAccessoryModel();

  const validationSchema = Yup.object({
    // name: Yup.string().required('Name is required')
  });

  const rhodiumWeight = accessoryModel.weights.find(
    w => w.configuration.platingType === GQLPlatingType.Rhodium
  );
  const roseGoldWeight = accessoryModel.weights.find(
    w => w.configuration.platingType === GQLPlatingType.RoseGold
  );
  const yellowGoldWeight = accessoryModel.weights.find(
    w => w.configuration.platingType === GQLPlatingType.YellowGold
  );

  const initialValues = {
    sizeType: accessoryModel.sizeType,
    dimensions: {
      width: accessoryModel.dimensions.width,
      height: accessoryModel.dimensions.height,
      depth: accessoryModel.dimensions.depth
    },
    weights: {
      rhodium: rhodiumWeight ? rhodiumWeight.weightInGrams : 0,
      roseGold: roseGoldWeight ? roseGoldWeight.weightInGrams : 0,
      yellowGold: yellowGoldWeight ? yellowGoldWeight.weightInGrams : 0
    },
    availableConfigurations: accessoryModel.availableConfigurations
  };

  const onSubmit = (props: any) => {
    const configurations: GQLAccessoryConfigurationInput[] = [];
    const weights: GQLAccessoryWeightInput[] = [];

    if (props.weights.rhodium > 0) {
      const c: GQLAccessoryConfigurationInput = {
        sizeType: accessoryModel.sizeType,
        materialType: GQLMaterialType.Silver,
        platingType: GQLPlatingType.Rhodium
      };

      weights.push({
        configuration: c,
        weightInGrams: Number.parseFloat(props.weights.rhodium)
      });
      configurations.push(c);
    }

    if (props.weights.roseGold > 0) {
      const c: GQLAccessoryConfigurationInput = {
        sizeType: accessoryModel.sizeType,
        materialType: GQLMaterialType.Silver,
        platingType: GQLPlatingType.RoseGold
      };

      weights.push({
        configuration: c,
        weightInGrams: Number.parseFloat(props.weights.roseGold)
      });
      configurations.push(c);
    }

    if (props.weights.yellowGold > 0) {
      const c: GQLAccessoryConfigurationInput = {
        sizeType: accessoryModel.sizeType,
        materialType: GQLMaterialType.Silver,
        platingType: GQLPlatingType.YellowGold
      };

      weights.push({
        configuration: c,
        weightInGrams: Number.parseFloat(props.weights.yellowGold)
      });
      configurations.push(c);
    }

    const input: GQLAccessoryModelInput = {
      availableConfigurations: configurations,
      dimensions: {
        width: Number.parseFloat(props.dimensions.width),
        height: Number.parseFloat(props.dimensions.height),
        depth: Number.parseFloat(props.dimensions.depth)
      },
      sizeType: props.sizeType,
      weights: weights
    };

    const mutationVariables = { accessoryId: accessoryId, accessoryModelId: accessoryModel.id, input: input };

    saveAccessoryModel({ variables: mutationVariables }).then(_result => {
      onClose(true);
    });
  };

  return (
    <Dialog open aria-labelledby="model-dialog">
      <DialogTitle>{`Update accessory model`}</DialogTitle>
      <DialogContent>
        <div>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ values, handleSubmit }) => (
              <form onSubmit={handleSubmit} className="flex flex-col">
                <Field
                  name="sizeType"
                  component={JwlSelect}
                  label="Size Type"
                  options={availableAccessorySizes}
                />
                <ErrorMessage component="span" name="sizeType" className="fs-12 text-error" />

                <div style={{ height: '200px' }}>
                  <span>Dimensions</span>
                  <Field name="dimensions.height" component={JwlField} label="Height (mm)" />
                  <ErrorMessage component="span" name="dimensions.height" className="fs-12 text-error" />

                  <Field name="dimensions.width" component={JwlField} label="Width (mm)" />
                  <ErrorMessage component="span" name="dimensions.width" className="fs-12 text-error" />

                  <Field name="dimensions.depth" component={JwlField} label="Depth (mm)" />
                  <ErrorMessage component="span" name="dimensions.depth" className="fs-12 text-error" />
                </div>

                <div style={{ height: '200px' }}>
                  <span>Availability (weight === 0 means not available)</span>
                  <Field name={`weights.rhodium`} component={JwlField} label="Rhodium (g)" />
                  <ErrorMessage component="span" name={`weights.rhodium`} className="fs-12 text-error" />

                  <Field name={`weights.roseGold`} component={JwlField} label="Rose Gold (g)" />
                  <ErrorMessage component="span" name={`weights.roseGold`} className="fs-12 text-error" />

                  <Field name={`weights.yellowGold`} component={JwlField} label="Yellow Gold (g)" />
                  <ErrorMessage component="span" name={`weights.yellowGold`} className="fs-12 text-error" />
                </div>

                <DialogActions>
                  <Button color="primary" onClick={() => onClose(false)}>
                    Close
                  </Button>
                  <Button type="submit" color="primary" variant="contained">
                    Save
                  </Button>
                </DialogActions>
              </form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccessoryModelUpdateDialog;
